import { AxiosError } from 'axios';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@crema/redux/store';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useInfoViewActionsContext } from '@crema/context/InfoViewContextProvider';
import { UserType } from '@crema/models/account';
import { AuthUserType } from '@crema/models/AuthUser';
import { setUser } from '@crema/redux/store/userSlice';
import jwtAxios, { setJwtAccessToken, setLanguage, setUserInfo } from './index';
import { resetAllCaches } from '@crema/redux/query';
import * as process from 'process';
import { deleteIndexedDB, saveIndexedDB } from '@libs/helpers/src';
import { getLocalStorage, LOCAL_STORAGE_NAMES, removeLocalStorage } from '@libs/storage/src/lib/local';
import { SESSION_STORAGE_NAMES, setSessionStorage } from '@libs/storage/src/lib/session';
import { useAuthMenusQuery } from '@libs/services/auth/src/react-query/useAuthMenusQuery';
import { useAuthUserQuery } from '@libs/services/auth/src/react-query/useAuthUserQuery';
import Cookies from 'js-cookie';

interface JWTAuthContextProps {
  user: AuthUserType | UserType | undefined;
  menus: any[] | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
}

export interface SignInProps {
  email: string;
  password: string | null;
  snsId?: string;
  snsType?: string;
  channelId?: string | undefined;
  isChannelAdmin?: boolean;
  mallId?: string;
  shopNo?: string;
  cafe24UserId?: string;
  naverStoreAccountUid?: string;
  naverStoreAccountRole?: string;
  shopbyMallNo?: string;
  shopbyUserId?: string;
  // 소셜(카카오/네이버) 로그인에서만 전달되는 추가 정보. 구글/이메일 로그인은 미전송.
  gender?: string; // 'M' | 'F'
  birthDay?: string; // 'YYYYMMDD'
  mktAgree?: boolean;
}

export interface ChannelAdminSignUpProps {
  username: string;
  email: string;
  password: string | null;
  phone: string;
  emailMarketingAgreed: boolean;
  smsMarketingAgreed: boolean;
  mallId?: string;
  shopNo?: string;
  cafe24UserId?: string;
  naverStoreAccountUid?: string;
  naverStoreAccountRole?: string;
  shopbyMallNo?: string;
  shopbyUserId?: string;
  snsId?: string;
  snsType?: string;
  invitationCode?: string;
  // 소셜(카카오/네이버) 로그인에서만 전달되는 추가 정보. 구글/이메일 가입은 미전송.
  gender?: string; // 'M' | 'F'
  birthDay?: string; // 'YYYYMMDD'
  mktAgree?: boolean;
}

interface JWTAuthActionsProps {
  signUpUser: (data: SignUpProps) => void;
  channelAdminSignUpUser: (
    data: ChannelAdminSignUpProps,
  ) => Promise<{ success: boolean; error?: string | undefined } | undefined>;
  signInUser: (
    data: SignInProps,
  ) => Promise<{ success?: boolean; error?: string | undefined; passwordExpired?: boolean } | undefined>;
  logout: () => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: undefined,
  menus: undefined,
  isAuthenticated: false,
  isLoading: true,
});
const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signUpUser: () => {},
  channelAdminSignUpUser: () => Promise.resolve({ success: false, error: undefined }),
  signInUser: () => Promise.resolve({ success: false, error: undefined }),
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthProviderProps {
  children: ReactNode;
  onLogout?: () => void; // 로그아웃 시 호출될 optional callback
}

const JWTAuthProvider: React.FC<JWTAuthProviderProps> = ({ children, onLogout }) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const domain = useSelector((state: RootState) => state.app.domain);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [jwtAuthData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: undefined,
    menus: undefined,
    isAuthenticated: false,
    isLoading: true,
  });

  const infoViewActionsContext = useInfoViewActionsContext();

  const { data: authMenus, error: authMenuError, refreshData: refreshAuthMenus } = useAuthMenusQuery();

  const { data: userAuthData, error: userAuthDataError, refreshData: refreshAuthUser } = useAuthUserQuery();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setJWTAuthData({
        user: undefined,
        menus: undefined,
        isLoading: false,
        isAuthenticated: false,
      });
      return;
    }

    // me API 요청 성공
    if (userAuthData) {
      setUserInfo(userAuthData);
      setLanguage(userAuthData.langKey);
      // setUserData(userAuthData);
      dispatch(setUser(userAuthData as UserType));

      // jwtAuthData의 user 부분을 즉시 업데이트하여 리렌더링 트리거
      setJWTAuthData((prev) => ({
        ...prev,
        user: userAuthData,
        isAuthenticated: true,
      }));

      // 채널어드민이면서 path가 '/loading', '/cafe24Auth'이 아닌 경우 체크 로직 진행
      // if (process.env.NX_APPLICATION === 'ac' && !['/loading', '/cafe24Auth'].includes(window.location.pathname)) {
      // if (userAuthData.channel.inflowPath === 'CAFE24' && !userAuthData.isCafe24AuthencationUser) {
      // 외부채널 승인이 안된 경우 채널 승인 페이지로 이동
      //   navigate('/cafe24Auth');
      // }
      // else if (
      //   window.location.pathname !== '/cafe24Auth' &&
      //   (userAuthData.channel?.productSuppliedStatus !== 'COMPLETED' || userAuthData.channel?.productSuppliedStatus !== 'IN_SUPPLY'  )
      // ) {
      //   // 상품 세팅이 안된 경우 로딩 페이지로 이동
      //   navigate('/loading');
      // }
      // }
    }

    if (userAuthDataError) {
      // me API 요청 실패
      setJWTAuthData({
        user: undefined,
        menus: undefined,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, [userAuthData, userAuthDataError]);

  useEffect(() => {
    if (authMenus) {
      // me API 요청 성공 후, menu API 요청까지 성공하고 받은 유저 데이터
      // me 호출 후 menu호출하도록 수정 후 의존성 배열에서 authUser 삭제
      const firstMenu = authMenus.find((menu) => menu.display && !menu.path.includes('.'));
      // if(process.env.NX_INITIAL_URL) {
      //   sessionStorage.setItem('initialUrl', process.env.NX_INITIAL_URL);
      // }
      if (firstMenu) {
        const firstSubmenu = authMenus.find((menu) => menu.path.startsWith(firstMenu.path + '.'));
        const initialUrl = firstSubmenu ? firstSubmenu.url : firstMenu.url;

        // 초기 url을 설정해준다.
        sessionStorage.setItem('initialUrl', process.env.NX_APPLICATION === 'ac' ? '/main' : initialUrl);
      }

      setJWTAuthData({
        user: userAuthData,
        menus: authMenus,
        isLoading: false,
        isAuthenticated: true,
      });
    }
    if (authMenuError) {
      // menu API 요청 실패
      setJWTAuthData({
        user: userAuthData,
        menus: undefined,
        isLoading: false,
        isAuthenticated: true,
      });
    }
  }, [authMenus, authMenuError]);

  const signInUser = async ({ email, password, isChannelAdmin = false, ...rest }: SignInProps) => {
    infoViewActionsContext.fetchStart();
    try {
      const formData = new FormData();

      // 로그인 필수 값
      formData.append('username', email);
      formData.append('grant_type', 'password');
      password && formData.append('password', password);

      /* 
       나머지 필드 추가
       - 로그인 선택 값 (sns 로그인 시 필요)
         channelId && formData.append('channelId', channelId);
         snsId && formData.append('snsId', snsId);
         snsType && formData.append('snsType', snsType);
       */
      Object.entries(rest || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string);
        }
      });

      const endpoint = isChannelAdmin ? '/oauth/user/token' : '/oauth/token';

      const res = await jwtAxios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        auth: {
          username: process.env.NX_OAUTH2_USR,
          password: process.env.NX_OAUTH2_PWD,
        },
      });

      const { access_token, refresh_token, passwordExpired } = res.data;
      resetAllCaches(dispatch);

      // React Query 캐시 초기화
      // queryClient.clear();

      if (passwordExpired) {
        try {
          sessionStorage.setItem('passwordExpired', 'true');
          sessionStorage.setItem('passwordExpiredToken', access_token);
        } catch (e) {}
        infoViewActionsContext.fetchSuccess();
        // 비밀번호 만료 시 재설정 페이지로 이동. 호출부가 별도 네비게이션으로 덮어쓰지 않도록
        // passwordExpired 플래그를 반환한다.
        navigate(`/password-expired?token=${access_token}`, { replace: true });
        return { passwordExpired: true };
      }

      // 정상 로그인: 이전 세션에서 남아있을 수 있는 비밀번호 만료 플래그를 제거하여
      // 스테일 플래그로 인한 /password-expired 재리다이렉트를 방지한다.
      try {
        sessionStorage.removeItem('passwordExpired');
        sessionStorage.removeItem('passwordExpiredToken');
      } catch (e) {}

      setJwtAccessToken(access_token);
      saveIndexedDB('refresh-token', refresh_token);

      setJWTAuthData({
        user: res.data,
        menus: undefined,
        isAuthenticated: true,
        isLoading: false,
      });
      infoViewActionsContext.fetchSuccess();
      refreshAuthUser();

      return { success: true };
    } catch (error) {
      setJWTAuthData({
        ...jwtAuthData,
        menus: undefined,
        isAuthenticated: false,
        isLoading: false,
      });

      let errorMessage = String(messages['common.toast.fail']);

      if (error instanceof AxiosError) {
        const { response } = error;
        const errorKey = response?.data?.errorKey;

        if (errorKey) {
          const errorMessages: Record<string, string> = {
            multipleChannelsPerUser: errorKey,
            userNotFound: '가입된 이력이 없는 이메일입니다.',
            passwordIncorrect: '이메일 또는 비밀번호가 올바르지 않습니다.',
            userNotActivated: '정지된 회원입니다. 채널을 소유한 관리자에게 문의하세요.',
            userLocked: String(messages['signin.validation.accountLocked']),
            channelUserAlreadyInvited: '이미 초대된 사용자입니다.',
          };

          errorMessage = errorMessages[errorKey] || String(messages['signin.validation.wrongAuthInfo']);
        }
      }

      infoViewActionsContext.fetchError(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const channelAdminSignUpUser = async (payload: ChannelAdminSignUpProps) => {
    infoViewActionsContext.fetchStart();
    try {
      const endpoint = 'openapi/users';
      const result = await jwtAxios.post(endpoint, payload);

      infoViewActionsContext.fetchSuccess();
      return {
        success: true,
        data: result,
      };
    } catch (error) {
      setJWTAuthData({
        ...jwtAuthData,
        menus: undefined,
        isAuthenticated: false,
        isLoading: false,
      });

      let errorMessage = String(messages['common.toast.fail']);

      if (error instanceof AxiosError) {
        const { response } = error;
        const errorKey = response?.data?.errorKey;

        if (errorKey) {
          const errorMessages: Record<string, string> = {
            multipleChannelsPerUser: errorKey,
            userNotFound: '가입된 이력이 없는 이메일입니다.',
            passwordIncorrect: '이메일 또는 비밀번호가 올바르지 않습니다.',
            userNotActivated: '정지된 회원입니다. 채널을 소유한 관리자에게 문의하세요.',
            userLocked: String(messages['signin.validation.accountLocked']),
            channelUserAlreadyInvited: '이미 초대된 사용자입니다.',
            accountDeletedEmailReuseNotAllowed:
              '안전한 운영을 위해 탈퇴한 계정은 재사용이 불가능합니다.\n새로운 이메일로 가입해 주세요.',
          };

          errorMessage = errorMessages[errorKey] || String(messages['signin.validation.wrongAuthInfo']);
        }
      }

      infoViewActionsContext.fetchError(errorMessage);

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const signUpUser = async ({ name, email, password }: { name: string; email: string; password: string }) => {
    infoViewActionsContext.fetchStart();
    try {
      const { data } = await jwtAxios.post('users', { name, email, password });
      const res = await jwtAxios.get(`/auth`);
      setJWTAuthData({
        user: res.data,
        menus: undefined,
        isAuthenticated: true,
        isLoading: false,
      });
      infoViewActionsContext.fetchSuccess();
    } catch (error) {
      setJWTAuthData({
        ...jwtAuthData,
        menus: undefined,
        isAuthenticated: false,
        isLoading: false,
      });
      infoViewActionsContext.fetchError(String(messages['common.toast.fail']));
    }
  };

  const logout = async () => {
    removeLocalStorage(LOCAL_STORAGE_NAMES.TOKEN);

    await deleteIndexedDB('refresh-token');

    Cookies.remove('shop_no');
    // 로그아웃시 채널id 유지(아트클라우드))
    const channelId = jwtAuthData.user?.channel?.channelId;
    if (channelId) setSessionStorage(SESSION_STORAGE_NAMES.CHANNEL_ID, channelId);
    else sessionStorage.clear();

    setJwtAccessToken();
    // React Query 캐시 초기화
    queryClient.clear();
    // 앱별 로그아웃 처리 (art-cloud: Platform 정보 초기화 등)
    if (onLogout) {
      onLogout();
    }
    setJWTAuthData({
      user: undefined,
      menus: undefined,
      isLoading: false,
      isAuthenticated: false,
    });
    // 직접 로그아웃 시에는 현재 경로를 redirect-url로 저장하지 않고 로그인 페이지로 이동
    try {
      navigate('/signin', { replace: true });
    } catch (e) {}
  };

  return (
    <JWTAuthContext.Provider
      value={{
        ...jwtAuthData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signUpUser,
          channelAdminSignUpUser,
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthProvider;
